import { supabase, isSupabaseConfigured, resolveFunctionError } from '@/lib/supabase';

export interface VariantRef {
  id: string;
  product_id: string;
  size_id: string;
  sku: string;
}

export interface CheckoutCustomer {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  documentId: string;
  address: string;
  city: string;
  department: string;
}

export interface CheckoutPayload {
  customer: CheckoutCustomer;
  items: Array<{ variantId: string; quantity: number }>;
  paymentMethod: 'cod' | 'transfer' | 'epayco' | 'sistecredito';
  notes?: string;
  // Identificador compartido entre el pedido normal y el de producción de una
  // compra combinada. Permite sincronizar el pago sin depender de timestamps.
  groupId?: string;
  // Si este pedido debe sumar el flete de Interrapidísimo según la ciudad.
  chargeShipping?: boolean;
}

export interface CheckoutResult {
  orderNumber: string;
  orderId: string;
  total: number;
  customerId: string;
  status: string;
  // Identificador del grupo de compra combinada (pedido normal + producción).
  // Solo se llena cuando la compra mixta se dividió en dos pedidos.
  groupId?: string | null;
}

// Obtiene las variantes (id, producto, talla) para resolver el carrito.
// Lectura pública, disponible sin sesión.
export async function fetchVariants(): Promise<VariantRef[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('product_variants')
    .select('id, product_id, size_id, sku');
  if (error) {
    console.warn('[checkout] Failed to fetch variants:', error.message);
    return [];
  }
  return (data ?? []) as VariantRef[];
}

// Crea el pedido de forma transaccional a través del Edge Function
// `create-checkout-order`, que corre con service role (bypasea RLS de forma
// segura) y registra cliente + pedido + ítems + reserva de stock.
export async function createCheckoutOrder(payload: CheckoutPayload): Promise<CheckoutResult> {
  if (!supabase) {
    throw new Error('Supabase no está configurado');
  }
  const { data, error } = await supabase.functions.invoke('create-checkout-order', {
    body: payload,
  });
  if (error) {
    throw new Error(await resolveFunctionError(error));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data as CheckoutResult;
}

// Crea el pedido de producción a través del Edge Function `create-production-order`
// (service role). A diferencia del pedido normal, NO valida stock: los productos
// agotados se "mandan a hacer". El pedido nace con estado "para_produccion".
export async function createProductionOrder(payload: CheckoutPayload): Promise<CheckoutResult> {
  if (!supabase) {
    throw new Error('Supabase no está configurado');
  }
  const { data, error } = await supabase.functions.invoke('create-production-order', {
    body: payload,
  });
  if (error) {
    throw new Error(await resolveFunctionError(error));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data as CheckoutResult;
}